/**
 * Small vendor booking-ops modals: reschedule, private notes, and a generic
 * reason capture (replaces window.prompt for decline/no-show).
 */
import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { form_error_message } from '@/lib/forms';
import { use_reschedule_booking, use_set_vendor_notes } from '@/features/bookings/hooks';
import type { Booking } from '@/features/bookings/types';

const field = 'flex h-11 w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15';

function Shell({ title, on_close, children }: { title: string; on_close: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={on_close}>
      <div className="my-12 w-full max-w-md rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-5">{children}</div>
      </div>
    </div>
  );
}

/** datetime-local value (local wall-clock) → ISO string. */
function to_iso(local: string): string {
  return new Date(local).toISOString();
}

export function RescheduleModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const m = use_reschedule_booking();
  const [value, set_value] = useState('');
  const [error, set_error] = useState<string | null>(null);

  async function submit() {
    set_error(null);
    if (!value) { set_error('Pick a new date and time.'); return; }
    try {
      await m.mutateAsync({ id: booking.id, slot_start: to_iso(value) });
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'That slot is not available.'));
    }
  }

  return (
    <Shell title="Reschedule booking" on_close={on_close}>
      <p className="text-sm text-muted-foreground">Move this booking to another open slot. The customer is notified.</p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reschedule-slot">New date & time</Label>
        <input id="reschedule-slot" type="datetime-local" className={field} value={value} onChange={(e) => set_value(e.target.value)} data-testid="reschedule-slot" />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="reschedule-error">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={on_close}>Cancel</Button>
        <Button variant="gradient" onClick={submit} disabled={m.isPending} data-testid="reschedule-submit">
          {m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reschedule
        </Button>
      </div>
    </Shell>
  );
}

export function NotesModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const m = use_set_vendor_notes();
  const [value, set_value] = useState(booking.vendor_notes ?? '');
  const [error, set_error] = useState<string | null>(null);

  async function save() {
    set_error(null);
    try {
      await m.mutateAsync({ id: booking.id, vendor_notes: value });
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not save notes.'));
    }
  }

  return (
    <Shell title="Private notes" on_close={on_close}>
      <p className="text-sm text-muted-foreground">Only your team sees these — never shown to the customer.</p>
      <textarea className={`${field} h-32 resize-y py-2`} maxLength={2000} value={value} onChange={(e) => set_value(e.target.value)} data-testid="notes-textarea" />
      {error && <p className="text-sm text-destructive" data-testid="notes-error">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={on_close}>Cancel</Button>
        <Button variant="gradient" onClick={save} disabled={m.isPending} data-testid="notes-save">
          {m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save notes
        </Button>
      </div>
    </Shell>
  );
}

export function ReasonModal({
  title,
  label,
  pending,
  on_submit,
  on_close,
}: {
  title: string;
  label: string;
  pending: boolean;
  on_submit: (reason: string) => void;
  on_close: () => void;
}) {
  const [value, set_value] = useState('');
  return (
    <Shell title={title} on_close={on_close}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reason">{label}</Label>
        <textarea id="reason" className={`${field} h-24 resize-y py-2`} maxLength={500} value={value} onChange={(e) => set_value(e.target.value)} placeholder="Optional — shown to the customer" data-testid="reason-textarea" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={on_close}>Cancel</Button>
        <Button variant="destructive" onClick={() => on_submit(value.trim())} disabled={pending} data-testid="reason-submit">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm
        </Button>
      </div>
    </Shell>
  );
}
